import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  X,
  Loader2,
  AlertCircle,
  Image,
  Bold,
  Italic,
  Strikethrough,
  Code,
  Link,
  List,
  ListOrdered,
  Table,
  Quote,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Eye,
  Edit3,
  Split,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { uploadToR2 } from "@/lib/r2";
import Editor from "@monaco-editor/react";

const deleteFromR2 = async (path) => {
  try {
    const fullPath = `/${R2_CONFIG.BUCKET_NAME}/${path}`;
    const url = `${R2_CONFIG.ENDPOINT}${fullPath}`;

    const now = new Date();
    const amzDate = now.toISOString().replace(/[:-]|\.\d{3}/g, "");
    const dateStamp = amzDate.slice(0, 8);

    const host = new URL(R2_CONFIG.ENDPOINT).host;
    const region = "auto";
    const service = "s3";
    const algorithm = "AWS4-HMAC-SHA256";

    const payloadHash = await sha256Hex("");
    const canonicalUri = fullPath;
    const canonicalQuerystring = "";
    const canonicalHeaders = `host:${host}\nx-amz-content-sha256:${payloadHash}\nx-amz-date:${amzDate}\n`;
    const signedHeaders = "host;x-amz-content-sha256;x-amz-date";

    const canonicalRequest = [
      "DELETE",
      canonicalUri,
      canonicalQuerystring,
      canonicalHeaders,
      signedHeaders,
      payloadHash,
    ].join("\n");

    const credentialScope = `${dateStamp}/${region}/${service}/aws4_request`;
    const canonicalRequestHash = await sha256Hex(canonicalRequest);
    const stringToSign = [
      algorithm,
      amzDate,
      credentialScope,
      canonicalRequestHash,
    ].join("\n");

    const signingKey = await getSignatureKey(
      R2_CONFIG.SECRET_KEY,
      dateStamp,
      region,
      service
    );
    const signatureBytes = await crypto.subtle.sign(
      "HMAC",
      await crypto.subtle.importKey(
        "raw",
        signingKey,
        { name: "HMAC", hash: "SHA-256" },
        false,
        ["sign"]
      ),
      new TextEncoder().encode(stringToSign)
    );
    const signature = bufferToHex(signatureBytes);

    const authorizationHeader = `${algorithm} Credential=${R2_CONFIG.ACCESS_KEY}/${credentialScope}, SignedHeaders=${signedHeaders}, Signature=${signature}`;

    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        Host: host,
        Authorization: authorizationHeader,
        "x-amz-date": amzDate,
        "x-amz-content-sha256": payloadHash,
      },
    });

    return response.ok;
  } catch (error) {
    console.error("R2 Delete Error:", error);
    return false;
  }
};

// ============= UI COMPONENTS =============
const Separator = ({ orientation = "horizontal", className = "" }) => {
  const orientationClass =
    orientation === "vertical" ? "h-full w-px" : "h-px w-full";
  return (
    <div
      className={`shrink-0 bg-(--neutral-300) ${orientationClass} ${className}`}
    />
  );
};

export const CodeEditor = ({
  value,
  onChange,
  placeholder,
  onInsert,
  onMount,
}) => {
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const [theme, setTheme] = useState("vs-light");

  // Set initial base theme (will be replaced with project-specific theme
  // once Monaco mounts). We don't keep observing here because Monaco will
  // handle re-definition when dark mode changes after mount.
  useEffect(() => {
    const isDark = document.documentElement.classList.contains("dark");
    setTheme(isDark ? "vs-dark" : "vs");
  }, []);

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    // Define and apply a theme that uses the project's background colors
    // while inheriting the rest of the VS/VS-Dark token colors.
    const defineAndApplyTheme = () => {
      const isDark = document.documentElement.classList.contains("dark");
      const cssVar = isDark ? "--neutral-100" : "--surface-panel";
      let bg = getComputedStyle(document.documentElement).getPropertyValue(
        cssVar
      );
      bg = (bg || (isDark ? "#0b1220" : "#ffffff")).trim();

      const base = isDark ? "vs-dark" : "vs";
      const themeName = isDark ? "zindua-project-dark" : "zindua-project-light";

      try {
        monaco.editor.defineTheme(themeName, {
          base,
          inherit: true,
          rules: [],
          colors: {
            "editor.background": bg,
          },
        });
        monaco.editor.setTheme(themeName);
        setTheme(themeName);
      } catch (e) {
        // If monaco isn't ready or theme registration fails, fall back
        monaco.editor.setTheme(base);
        setTheme(base);
      }
    };

    defineAndApplyTheme();

    // Observe dark mode changes and re-define the theme with new bg color
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === "class") {
          defineAndApplyTheme();
        }
      }
    });
    observer.observe(document.documentElement, { attributes: true });
    // keep observer reference on editor so cleanup happens on unmount
    editor._zinduaThemeObserver = observer;
    if (onMount) {
      onMount(editor, monaco);
    }
  };

  // Clean up observer when editor component unmounts
  useEffect(() => {
    return () => {
      const editor = editorRef.current;
      if (editor && editor._zinduaThemeObserver) {
        editor._zinduaThemeObserver.disconnect();
        delete editor._zinduaThemeObserver;
      }
    };
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Tab") {
      e.preventDefault();
      editorRef.current.trigger("keyboard", "tab", null);
      return;
    }

    if (onInsert && (e.ctrlKey || e.metaKey)) {
      let handled = true;
      switch (e.key) {
        case "b":
          onInsert({ before: "**", after: "**", placeholder: "bold text" });
          break;
        case "i":
          onInsert({ before: "*", after: "*", placeholder: "italic text" });
          break;
        case "k":
          onInsert({ before: "[", after: "](url)", placeholder: "link text" });
          break;
        case "l":
          onInsert({ before: "- ", after: "", placeholder: "list item" });
          break;
        default:
          handled = false;
      }
      if (handled) e.preventDefault();
    }

    if (onInsert && e.altKey) {
      let handled = true;
      switch (e.key) {
        case "s":
          onInsert({ before: "~~", after: "~~", placeholder: "strikethrough" });
          break;
        case "e":
          onInsert({ before: "`", after: "`", placeholder: "code" });
          break;
        case "o":
          onInsert({ before: "1. ", after: "", placeholder: "numbered item" });
          break;
        case "q":
          onInsert({ before: "> ", after: "", placeholder: "blockquote" });
          break;
        case "1":
          onInsert({ before: "# ", after: "", placeholder: "Heading 1" });
          break;
        case "2":
          onInsert({ before: "## ", after: "", placeholder: "Heading 2" });
          break;
        case "3":
          onInsert({ before: "### ", after: "", placeholder: "Heading 3" });
          break;
        default:
          handled = false;
      }
      if (handled) e.preventDefault();
    }
  };

  return (
    <div className="relative h-full">
      <Editor
        height="100%"
        language="markdown"
        theme={theme}
        value={value || ""}
        onChange={onChange}
        onMount={handleEditorDidMount}
        options={{
          minimap: { enabled: false },
          wordWrap: "on",
          fontSize: 15,
          lineNumbers: "on",
          glyphMargin: false,
          folding: false,
          lineDecorationsWidth: 0,
          lineNumbersMinChars: 0,
          scrollBeyondLastLine: false,
          automaticLayout: true,
          padding: { top: 16, bottom: 16 },
          quickSuggestions: false,
        }}
      />
    </div>
  );
};

export const Toolbar = ({
  onInsert,
  viewMode,
  setViewMode,
  onImageUpload,
  isUploading,
}) => {
  const fileInputRef = useRef(null);

  const formatButtons = [
    {
      icon: Bold,
      before: "**",
      after: "**",
      placeholder: "bold text",
      title: "Bold (Ctrl+B)",
    },
    {
      icon: Italic,
      before: "*",
      after: "*",
      placeholder: "",
      title: "Italic (Ctrl+I)",
    },
    {
      icon: Strikethrough,
      before: "~~",
      after: "~~",
      placeholder: "",
      title: "Strikethrough",
    },
    {
      icon: Code,
      before: "`",
      after: "`",
      placeholder: "code",
      title: "Inline Code",
    },
  ];

  const headerButtons = [
    {
      icon: Heading2,
      before: "## ",
      after: "",
      placeholder: " ",
      title: "Heading 2",
    },
    {
      icon: Heading3,
      before: "### ",
      after: "",
      placeholder: "",
      title: "Heading 3",
    },
    {
      icon: Heading4,
      before: "#### ",
      after: "",
      placeholder: " ",
      title: "Heading 4",
    },
    {
      icon: Heading5,
      before: "### ",
      after: "",
      placeholder: " ",
      title: "Heading 5",
    },
  ];

  const contentButtons = [
    {
      icon: Link,
      before: "[",
      after: "](url)",
      placeholder: "link",
      title: "Link",
    },
    {
      icon: List,
      before: "- ",
      after: "",
      placeholder: " ",
      title: "Bullet List",
    },
    {
      icon: ListOrdered,
      before: "1. ",
      after: "",
      placeholder: " ",
      title: "Numbered List",
    },
    {
      icon: Quote,
      before: "> ",
      after: "",
      placeholder: " ",
      title: "Quote",
    },
    {
      icon: Table,
      action:
        "|  |  |\n|----------|----------|\n|    |    |",
      title: "Table",
    },
  ];

  return (
    <div className="flex items-center gap-2 p-3 border-b border-(--neutral-300) overflow-x-auto">
      <div className="flex items-center gap-1">
        {formatButtons.map((btn, i) => (
          <Button
            key={i}
            variant="ghost"
            size="lg"
            onClick={() => onInsert(btn)}
            title={btn.title}
            className="h-10 w-10 p-0"
          >
            <btn.icon className="h-4 w-4" />
          </Button>
        ))}
      </div>

      <Separator orientation="vertical" className="mx-2 h-6" />

      <div className="flex items-center gap-1">
        {headerButtons.map((btn, i) => (
          <Button
            key={i}
            variant="ghost"
            size="lg"
            onClick={() => onInsert(btn)}
            title={btn.title}
            className="h-10 w-10 p-0"
          >
            <btn.icon className="h-4 w-4" />
          </Button>
        ))}
      </div>

      <Separator orientation="vertical" className="mx-2 h-6" />

      <div className="flex items-center gap-1">
        {contentButtons.map((btn, i) => (
          <Button
            key={i}
            variant="ghost"
            size="lg"
            onClick={() => onInsert(btn.action ? { action: btn.action } : btn)}
            title={btn.title}
            className="h-10 w-10 p-0"
          >
            <btn.icon className="h-4 w-4" />
          </Button>
        ))}

        <Button
          variant="ghost"
          size="lg"
          onClick={() => fileInputRef.current?.click()}
          title="Upload Image to R2"
          className="h-10 w-10 p-0"
          disabled={isUploading}
        >
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin text-(--color-accent)" />
          ) : (
            <Image className="h-4 w-4" />
          )}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={onImageUpload}
          className="hidden"
        />
      </div>

      <Separator orientation="vertical" className="mx-2 h-6" />

      <div className="flex items-center gap-1">
        <Button
          variant={viewMode === "edit" ? "default" : "ghost"}
          size="lg"
          onClick={() => setViewMode("edit")}
          title="Edit Mode"
          className="h-8 w-8 p-0"
        >
          <Edit3 className="h-4 w-4" />
        </Button>
        <Button
          variant={viewMode === "split" ? "default" : "ghost"}
          size="lg"
          onClick={() => setViewMode("split")}
          title="Split Mode"
          className="h-8 w-8 p-0"
        >
          <Split className="h-4 w-4" />
        </Button>
        <Button
          variant={viewMode === "preview" ? "default" : "ghost"}
          size="lg"
          onClick={() => setViewMode("preview")}
          title="Preview Mode"
          className="h-8 w-8 p-0"
        >
          <Eye className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// Simple markdown renderer for preview
const SimpleMarkdownPreview = ({ content }) => {
  return (
    <div className="prose dark:prose-invert max-w-none">
      <pre className="whitespace-pre-wrap">{content}</pre>
    </div>
  );
};
